// Pure, React-free selector for the "Requests need action" My Day section
// (#1600). Derives up to four signals per request from the actor-scoped
// `GET /requests` rows + the chat-unread summary, dedups to one row per
// request, picks a primary signal, and orders the section.
//
// PII BOUNDARY (#1600 AC-9): the wire `Request` rows carry full business
// detail (name, route, addresses, insurance, chat, items, carriers...).
// This module's OUTPUT (`MyDayRequestSignalRow`) intentionally projects
// ONLY `{ id, status, slaDeadline }` + client-derived signal metadata, so
// nothing downstream (view-model, component state, render) can leak PII.
import type { RequestListRow } from '@magimp/shared';
import { RequestCarrierState, RequestStatus } from '@magimp/shared';
import { formatSLA, type SLALevel } from '../../../i18n/index.js';

// WHY (#1600 AC-4): exact candidate status set. ARCHIVE / CLOSED / SUCCESS
//   / REFUSED / DRAFT are excluded — they are never actionable on My Day.
export const MY_DAY_CANDIDATE_STATUSES = [
  RequestStatus.READY_FOR_WORK,
  RequestStatus.IN_WORK,
  RequestStatus.PROCESSED,
  RequestStatus.RETURN,
] as const;

// The four derived signal kinds (#1600 FR-2). Ordered here by section
// priority (FR-3 / AC-3): Returned, then SLA hot/overdue, then ready to
// pick a winner, then unread chat. `SIGNAL_PRIORITY` below indexes this.
export type MyDayRequestSignalKind = 'returned' | 'slaHot' | 'readyToPick' | 'unreadChat';

const SIGNAL_PRIORITY: readonly MyDayRequestSignalKind[] = [
  'returned',
  'slaHot',
  'readyToPick',
  'unreadChat',
];

// Non-PII projection of one request that matched ≥1 signal. The only
// request fields carried are `id` (the public `LR-#####` code — there is
// no separate `publicCode`), `status`, and the raw `slaDeadline` ISO
// string used to render the SLA badge. `signals` is fully client-derived.
export interface MyDayRequestSignalRow {
  id: string;
  status: RequestStatus;
  slaDeadline: string | null;
  // Every matched signal, in `SIGNAL_PRIORITY` order, so the row can render
  // one badge per signal without re-deriving.
  signals: readonly MyDayRequestSignalKind[];
  // Highest-priority matched signal — the row's primary action/label.
  primary: MyDayRequestSignalKind;
}

// Read-side unread map shape (matches `UnreadSummaryEntry` in
// `requests/queries.read.ts` without importing the hook into this pure
// module). Only `unreadCount` is consulted here.
export interface MyDayUnreadLookup {
  get(id: string): { unreadCount: number } | undefined;
}

function isReadyToPick(req: RequestListRow): boolean {
  if (req.status !== RequestStatus.IN_WORK && req.status !== RequestStatus.PROCESSED) return false;
  return req.carriers.some((c) => c.state === RequestCarrierState.ANSWERED);
}

function slaLevel(req: RequestListRow, now: number): SLALevel {
  // WHY (#1600 FR-2.2): reuse the canonical SLA classifier rather than
  //   re-deriving thresholds. We only consume `.level`; the localized
  //   `.text` it also produces is discarded (rendering owns its own copy).
  return formatSLA(
    {
      status: req.status,
      slaDeadline: toEpoch(req.slaDeadline),
      inWorkAt: toEpoch(req.inWorkAt),
      closedAt: toEpoch(req.closedAt),
      processedAt: toEpoch(req.processedAt),
    },
    now,
  ).level;
}

function isSlaHot(req: RequestListRow, now: number): boolean {
  if (req.status !== RequestStatus.READY_FOR_WORK && req.status !== RequestStatus.IN_WORK) {
    return false;
  }
  const level = slaLevel(req, now);
  return level === 'warn' || level === 'err';
}

function toEpoch(iso: string | null): number | null {
  if (iso === null) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}

function signalsFor(
  req: RequestListRow,
  unread: MyDayUnreadLookup,
  now: number,
): MyDayRequestSignalKind[] {
  const out: MyDayRequestSignalKind[] = [];
  if (req.status === RequestStatus.RETURN) out.push('returned');
  if (isSlaHot(req, now)) out.push('slaHot');
  if (isReadyToPick(req)) out.push('readyToPick');
  if ((unread.get(req.id)?.unreadCount ?? 0) > 0) out.push('unreadChat');
  // Normalize to canonical priority order so badges + primary are stable.
  return SIGNAL_PRIORITY.filter((kind) => out.includes(kind));
}

// WHY (#1600 AC-3): deterministic section ordering keyed on the row's
//   primary (highest-priority) signal. Within a primary group rows keep a
//   stable order by id so the list does not reshuffle between renders.
function compareRows(a: MyDayRequestSignalRow, b: MyDayRequestSignalRow): number {
  const pa = SIGNAL_PRIORITY.indexOf(a.primary);
  const pb = SIGNAL_PRIORITY.indexOf(b.primary);
  if (pa !== pb) return pa - pb;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * @aiPurpose derive the ordered, deduped, non-PII "Requests need action"
 *   rows from actor-scoped request rows + the unread map.
 * @aiSecurity (#1600 AC-9) output rows carry ONLY `{ id, status,
 *   slaDeadline }` + client-derived signals — never request name, route,
 *   addresses, phones, insurance, refusal text, chat, items, or carrier
 *   detail. Callers must not re-introduce PII into My-Day state.
 * @aiInvariant input is filtered to `MY_DAY_CANDIDATE_STATUSES` even when
 *   the caller already scoped the query, so an excluded status can never
 *   slip into the section.
 */
export function selectMyDayRequestSignals(
  requests: readonly RequestListRow[],
  unread: MyDayUnreadLookup,
  now: number = Date.now(),
): MyDayRequestSignalRow[] {
  const rows: MyDayRequestSignalRow[] = [];
  for (const req of requests) {
    if (!(MY_DAY_CANDIDATE_STATUSES as readonly RequestStatus[]).includes(req.status)) continue;
    const signals = signalsFor(req, unread, now);
    if (signals.length === 0) continue;
    // Dedup is structural: one row per request id with every matched
    // signal collapsed into `signals` (FR-3). `primary` is `signals[0]`
    // because `signalsFor` already sorted by priority.
    rows.push({
      id: req.id,
      status: req.status,
      slaDeadline: req.slaDeadline,
      signals,
      primary: signals[0]!,
    });
  }
  return rows.sort(compareRows);
}
